import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import NavSidebar from "../components/NavSidebar";
import { navActiveKeyForModality } from "../components/NavSidebarGrouped";
import ConversationPage from "../components/conversation/ConversationPage";
import {
  formatTimeOnlySafe, getMsgSide, getMsgText, getMsgTime,
} from "../components/conversation/lib/messageShape";
import VoiceCallDetails from "../components/VoiceCallDetails";
import ConversationDetailPane from "../components/conversation/detail/ConversationDetailPane";
import ConversationIntelFilters from "../components/conversation/list/ConversationIntelFilters";
import ListEndMarker from "../components/conversation/list/ListEndMarker";
import RowIntel from "../components/conversation/list/RowIntel";
import ChatRowIntel from "../components/conversation/list/ChatRowIntel";
import PanelFilterChip from "../components/conversation/list/PanelFilterChip";
import { usePanelFilter, applyPanelFilterTo } from "../components/conversation/list/usePanelFilter";
import { useIntelEnrichment } from "../components/conversation/list/useIntelEnrichment";
import { displayNameFor } from "../components/conversation/lib/visitorLabel";
import {
  DEFAULT_INTEL_FILTERS,
  applyIntelFilters,
  mergeIntel,
  intelExportCells,
  INTEL_EXPORT_HEADERS,
  intelExportPairs,
} from "../components/conversation/list/conversationIntel";
import { upperLabel } from "../components/shared/format";
import { useNotification } from "../contexts/NotificationContext";

// The detail toolbar's three buttons. Shared so they stay identical, and given a
// min-width plus centred content so they are the SAME size rather than each
// shrink-wrapping its own label ("Tag" was half the width of "Export CSV").
// Shared button geometry, so the list-header exports and the detail toolbar are
// the same size rather than two families of control on one screen.
// `max-md:min-h-[44px]` is the spec's TOUCH floor, so it applies where the
// control is reached by finger and leaves the tuned desktop geometry alone.
const BUTTON_GEOMETRY =
  "inline-flex items-center justify-center gap-1.5 min-w-[7.5rem] px-3 py-1.5 max-md:min-h-[44px] rounded-lg text-sm font-medium transition-colors";

// Tinted rather than solid: same weight as the neutral toolbar buttons beside
// them, with the colour kept as an identity cue (CSV green, PDF blue) instead of
// a solid fill that shouted louder than anything else in the header.
// One colour per action, used wherever that action appears: CSV is green, PDF is
// purple, Tag is amber. The list header and the detail toolbar therefore agree —
// One treatment for every button in the conversation panel — the brand blue the
// Tag button uses. The action is named on the button; the colour is the app's,
// not a per-action code. Outlined rather than filled so a row of them stays quiet.
const ACTION_BUTTON =
  `${BUTTON_GEOMETRY} border border-[#32B1FF]/40 bg-[#32B1FF]/10 text-[#2175A8] dark:text-[#7fd0ff] hover:bg-[#32B1FF]/20`;
const TAG_BUTTON = ACTION_BUTTON;
const BULK_CSV_BUTTON = ACTION_BUTTON;
const BULK_PDF_BUTTON = ACTION_BUTTON;
const EXPORT_CSV_BUTTON = ACTION_BUTTON;
const EXPORT_PDF_BUTTON = ACTION_BUTTON;
// The button styling this page had before Voice Intelligence. The plain
// transcript list (a modality URL with no view=intelligence) still renders it;
// only the analysis band (My Agents → Chat/Voice) gets the unified treatment
// above. Copied verbatim from 817c6c9 so the two surfaces are not
// "close enough" but identical to what shipped.
const LEGACY_BULK_CSV_BUTTON = (busy) =>
  `px-2.5 py-1.5 rounded-lg text-[10px] font-semibold shadow-sm transition-all duration-200 flex items-center gap-1.5 ${
    busy
      ? "bg-gray-300 dark:bg-gray-700 text-gray-100 dark:text-gray-400 cursor-not-allowed"
      : "bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-md"
  }`;
const LEGACY_BULK_PDF_BUTTON = (busy) =>
  `px-2.5 py-1.5 rounded-lg text-white text-[10px] font-semibold shadow-sm transition-all duration-200 flex items-center gap-1.5 ${
    busy
      ? "bg-gray-300 dark:bg-gray-700 text-gray-100 dark:text-gray-400 cursor-not-allowed"
      : "bg-blue-600 hover:bg-blue-700 hover:shadow-md"
  }`;
const LEGACY_TOOLBAR_BUTTON =
  "px-3 py-1.5 rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-gray-100";
const LEGACY_TAG_BUTTON = `${LEGACY_TOOLBAR_BUTTON} flex items-center gap-1.5`;

const BUTTON_DISABLED =
  `${BUTTON_GEOMETRY} border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500 cursor-not-allowed`;

import { useView } from "../contexts/ViewContext";
import {
  getTranscripts,
  downloadTranscriptsCsv,
  transcriptsExportTitle,
  updateConversation,
  analyzeConversationTag,
  generateAiSummary,
  batchAnalyzeConversations,
  getProjectDetails,
  getConversationMessages
} from "../api";

/* export deps — jspdf + jspdf-autotable loaded on demand in the PDF handlers */
import Papa from "papaparse";

const formatYYYYMMDD = (d) => {
  // Use local time methods to respect user's timezone
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const tryParseDate = (ts) => {
  if (!ts) return null;
  if (typeof ts === "number") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof ts === "string") {
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) return d;
    const d2 = new Date(ts.replace(" ", "T"));
    if (!Number.isNaN(d2.getTime())) return d2;
  }
  return null;
};
// `formatTimeOnlySafe`, `getMsgSide`, `getMsgText` and `getMsgTime` now live in
// components/conversation/lib/messageShape.js — ONE implementation, imported
// above. They were duplicated here and in the twin page, and the slide-over
// became a third reader of the same rules; three copies of "which side is this
// message on" is how the same conversation ends up rendered two ways.
const cleanTextForPDF = (text) => {
  if (!text) return "";
  return text
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/Ø=P/g, '')
    .replace(/\u00D8/g, '')
    .replace(/\u003D/g, '')
    .replace(/\u0050/g, '')
    .replace(/\u00DE/g, '')
    .replace(/[^\x00-\x7F]/g, '')
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
const normalizeMessages = (convoObj) => {
  const arr = convoObj?.conversation || convoObj?.messages || [];
  return Array.isArray(arr) ? arr : [];
};
const parseTime12hToMs = (s) => {
  if (!s || typeof s !== "string") return null;
  const match = s.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return null;
  let [, hStr, mStr, sStr, ampm] = match;
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const sec = sStr ? parseInt(sStr, 10) : 0;
  if (h < 1 || h > 12 || m > 59 || sec > 59) return null;
  const isPM = ampm.toUpperCase() === "PM";
  if (h === 12) h = 0;
  const hours24 = h + (isPM ? 12 : 0);
  return ((hours24 * 60 + m) * 60 + sec) * 1000;
};
const formatDelta = (ms) => {
  if (ms == null || ms < 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m later`;
  if (m > 0) return `${m}m ${s}s later`;
  return `${s}s later`;
};


// Format phone number to US format: +1 (XXX) XXX-XXXX
const formatPhoneNumber = (phone) => {
  if (!phone) return phone;
  // Remove all non-digit characters except leading +
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');
  
  // Handle US numbers (10 or 11 digits)
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length > 10) {
    // International format - just add spaces for readability
    const countryCode = hasPlus ? `+${digits.slice(0, digits.length - 10)}` : digits.slice(0, digits.length - 10);
    const areaCode = digits.slice(-10, -7);
    const firstPart = digits.slice(-7, -4);
    const lastPart = digits.slice(-4);
    return `${countryCode} (${areaCode}) ${firstPart}-${lastPart}`;
  }
  // Return original if not a recognizable format
  return phone;
};

const makeSnippet = (haystackLower, haystackRaw, queryLower, radius = 60) => {
  const idx = haystackLower.indexOf(queryLower);
  if (idx < 0) return null;
  const start = Math.max(0, idx - radius);
  const end = Math.min(haystackRaw.length, idx + queryLower.length + radius);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < haystackRaw.length ? "…" : "";
  return prefix + haystackRaw.slice(start, end).replace(/\s+/g, " ") + suffix;
};

function ProjectDetailPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const view = useView();
  const { showWarning } = useNotification();

  // ?modality=chat|voice — sidebar's Chat / Voice links land here with the
  // filter applied. conversations.modality is the actual chat/voice split
  // field (kind is always 'ai_agent' — see 2026-07-24 probe).
  const modalityFilter = React.useMemo(() => {
    const raw = new URLSearchParams(location.search).get("modality");
    return raw === "chat" || raw === "voice" ? raw : null;
  }, [location.search]);

  // The analysis surface — the analysis band, the intelligence filters, the
  // enriched rows and the rebuilt detail pane — belongs to MY AGENTS → Chat and
  // → Voice, which carry `?view=intelligence`. The same page component reached by
  // a bare modality URL (no marker) keeps the plain transcript list it has always
  // had, so any link without the marker — an old bookmark — renders as before.
  // Everything new hangs off this one flag.
  const isIntelligence = React.useMemo(
    // BOTH analysis modalities. Develop introduced this gate for voice alone;
    // chat's band is the same surface reached by the same marker, so widening it
    // here keeps ONE flag rather than a second chat-shaped copy. The modality
    // clause stays so a hand-typed ?view=intelligence on any other modality
    // still changes nothing.
    () => new URLSearchParams(location.search).get("view") === "intelligence"
      && (modalityFilter === "voice" || modalityFilter === "chat"),
    [location.search, modalityFilter],
  );

  const projectNameFromState =
    location.state?.projectName ||
    location.state?.name ||
    location.state?.project?.name ||
    "";

  const projectLogoFromState =
    location.state?.projectLogo ||
    location.state?.logo ||
    location.state?.project?.logo ||
    null;

  useEffect(() => {
    if (projectNameFromState) {
      sessionStorage.setItem(`projectName:${projectId}`, projectNameFromState);
    }
    if (projectLogoFromState) {
      sessionStorage.setItem(`projectLogo:${projectId}`, projectLogoFromState);
    }
  }, [projectId, projectNameFromState, projectLogoFromState]);

  // Sync the URL's project into the global header filter. Arriving via a
  // project card or deep link would otherwise leave the header showing a
  // different org/property than the transcripts on screen.
  const { allProjects, selectedOrgId, selectedProjectId, selectOrg, selectProject } = view;
  useEffect(() => {
    if (!projectId || !allProjects?.length) return;
    const proj = allProjects.find((p) => String(p.id) === String(projectId));
    if (!proj) return;
    const projOrg = String(proj.organization_id || proj.company_id || "");
    if (projOrg && String(selectedOrgId || "") !== projOrg) selectOrg(projOrg);
    if (String(selectedProjectId || "") !== String(projectId)) selectProject(String(projectId));
  }, [projectId, allProjects, selectedOrgId, selectedProjectId, selectOrg, selectProject]);

  const projectName =
    projectNameFromState ||
    sessionStorage.getItem(`projectName:${projectId}`) ||
    "";

  const projectLogo =
    projectLogoFromState ||
    sessionStorage.getItem(`projectLogo:${projectId}`) ||
    null;

  const HEADER_H = 72;
  const BANNER_H = projectName ? 40 : 0;
  const PANEL_H = `calc(100vh - ${HEADER_H + BANNER_H}px)`;

  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptsError, setTranscriptsError] = useState("");
  const [transcripts, setTranscripts] = useState([]);
  // Progressive-load metadata so the Export button and "loaded X of Y" indicator
  // can show the true total while we stream pages in the background.
  const [totalConversations, setTotalConversations] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const TRANSCRIPT_PAGE_SIZE = 10;

  const [sortOrder, setSortOrder] = useState("desc");

  const [selectedId, setSelectedId] = useState(null);
  // Phone only: whether the detail sheet is open over the worklist. The page
  // auto-selects the first conversation on load, which is right for the desktop
  // two-pane layout but would drop a phone reader straight into a conversation
  // they never chose, with the list hidden behind it. Opening is therefore tied
  // to an explicit tap, not to `selectedId`.
  const [sheetOpen, setSheetOpen] = useState(false);
  // Messages for the selected conversation. The list-level fetch doesn't
  // include message bodies (only metadata), so fetch on demand from
  // conversation_messages when a row is picked.
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedMessagesLoading, setSelectedMessagesLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  useEffect(() => {
    if (!selectedId) { setSelectedMessages([]); setSelectedCall(null); return; }
    let cancelled = false;
    setSelectedMessagesLoading(true);
    getConversationMessages(selectedId)
      .then((resp) => {
        if (cancelled) return;
        setSelectedMessages(Array.isArray(resp?.data) ? resp.data : []);
        setSelectedCall(resp?.call || null);
      })
      .catch(() => { if (!cancelled) { setSelectedMessages([]); setSelectedCall(null); } })
      .finally(() => { if (!cancelled) setSelectedMessagesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);
  void selectedMessagesLoading;
  const selected = useMemo(
    () => transcripts.find((t) => t.id === selectedId) || null,
    [transcripts, selectedId]
  );

  const [conversationTags, setConversationTags] = useState({});
  const [taggingConversationId, setTaggingConversationId] = useState(null);
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);
  const availableTags = ["info_seeking", "availability_search", "ready_to_book", "post_booking_support", "customer_feedback", "spam_other"];
  const [showTagFiltersDialog, setShowTagFiltersDialog] = useState(false);
  
  // New filter states
  const [messageLengthFilter, setMessageLengthFilter] = useState("all");
  const [timeOfDayFilter, setTimeOfDayFilter] = useState("all");
  const [visitorTypeFilter, setVisitorTypeFilter] = useState("all");
  const [aiAnalysisFilter, setAiAnalysisFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  // Phase 7 — Voice Intelligence list filters, client-side over the loaded set.
  // One object rather than a state per filter: the whole set is passed to
  // ConversationIntelFilters and to applyIntelFilters, so adding a filter does
  // not mean threading another setter through the page.
  //
  // `intelMap` is fn_get_conversation_list keyed by conversation_id — the fields
  // the `getTranscripts` loader does not carry (outcome, stay type, urgency,
  // barriers, value, recording, caller_ever_spoke). Enrichment is additive: a
  // row the RPC omits keeps its existing render.
  const [intelFilters, setIntelFilters] = useState(DEFAULT_INTEL_FILTERS);
  
  // More filters modal state
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Project details state
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const aiEnabled = projectDetails?.ai_enabled || false;
  
  // AI Analysis states
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [showBatchProgress, setShowBatchProgress] = useState(false);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  
  // AI Cost Warning Modal
  const [showAiCostWarning, setShowAiCostWarning] = useState(false);
  const [aiCostAction, setAiCostAction] = useState('single');
  const [untaggedCount, setUntaggedCount] = useState(0);
  
  // Bulk AI Actions Menu
  const [showBulkAiMenu, setShowBulkAiMenu] = useState(false);
  
  // Error/Success Modal
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({ type: 'error', title: '', message: '' });

  // Show notification helper
  const showNotif = (type, title, message) => {
    setNotification({ type, title, message });
    setShowNotification(true);
  };

  const tagColors = {
    info_seeking: {
      bg: "bg-purple-100 dark:bg-purple-900/30",
      text: "text-purple-800 dark:text-purple-300",
      hoverBg: "hover:bg-purple-200 dark:hover:bg-purple-900/50",
      border: "border-purple-300 dark:border-purple-700",
      emoji: "ℹ️"
    },
    availability_search: {
      bg: "bg-blue-100 dark:bg-blue-900/30",
      text: "text-blue-800 dark:text-blue-300",
      hoverBg: "hover:bg-blue-200 dark:hover:bg-blue-900/50",
      border: "border-blue-300 dark:border-blue-700",
      emoji: "📅"
    },
    ready_to_book: {
      bg: "bg-green-100 dark:bg-green-900/30",
      text: "text-green-800 dark:text-green-300",
      hoverBg: "hover:bg-green-200 dark:hover:bg-green-900/50",
      border: "border-green-300 dark:border-green-700",
      emoji: "🎯"
    },
    post_booking_support: {
      bg: "bg-amber-100 dark:bg-amber-900/30",
      text: "text-amber-800 dark:text-amber-300",
      hoverBg: "hover:bg-amber-200 dark:hover:bg-amber-900/50",
      border: "border-amber-300 dark:border-amber-700",
      emoji: "🔧"
    },
    customer_feedback: {
      bg: "bg-teal-100 dark:bg-teal-900/30",
      text: "text-teal-800 dark:text-teal-300",
      hoverBg: "hover:bg-teal-200 dark:hover:bg-teal-900/50",
      border: "border-teal-300 dark:border-teal-700",
      emoji: "💬"
    },
    spam_other: {
      bg: "bg-red-100 dark:bg-red-900/30",
      text: "text-red-800 dark:text-red-300",
      hoverBg: "hover:bg-red-200 dark:hover:bg-red-900/50",
      border: "border-red-300 dark:border-red-700",
      emoji: "🚫"
    }
  };
  
  const getTagColors = (tag) => {
    if (!tag) return null;
    return tagColors[tag.toLowerCase()] || {
      bg: "bg-gray-100 dark:bg-gray-900/30",
      text: "text-gray-800 dark:text-gray-300",
      hoverBg: "hover:bg-gray-200 dark:hover:bg-gray-900/50",
      border: "border-gray-300 dark:border-gray-700"
    };
  };
  
  // Helper function to display tag in human-readable format (convert snake_case to Title Case)
  const capitalizeTag = (tag) => {
    if (!tag) return "";
    // Convert snake_case to Title Case: "info_seeking" → "Info Seeking"
    return tag
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const [query, setQuery] = useState("");
  // Click-to-filter from the analysis band (topic bars, question rows). One hook
  // for every panel that can raise a filter, shared with the client page.
  const listTopRef = useRef(null);
  const { panelFilter, applyPanelFilter, clearPanelFilter } = usePanelFilter(listTopRef);
  // Date range is driven by the global header filter (ViewContext) — the in-page
  // From/To inputs and quick presets have been removed in favor of the single source of truth.
  const startDate = view.rangeStart;
  const endDate = view.rangeEnd;
  const [forceRefresh, setForceRefresh] = useState(false);
  const [targetDateFilter, setTargetDateFilter] = useState(null); // For client-side date filtering
  const [loadingProgress, setLoadingProgress] = useState(null); // For batch loading progress

  const handleAddTag = async (conversationId, tag) => {
    try {
      setConversationTags((prev) => {
        return { ...prev, [conversationId]: tag };
      });
      setTaggingConversationId(null);
      
      await updateConversation(conversationId, projectId, { tag });
    } catch (error) {
      console.error("Failed to update conversation tag:", error);
    }
  };

  const handleRemoveTag = async (conversationId) => {
    try {
      setConversationTags((prev) => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
      
      await updateConversation(conversationId, projectId, { tag: "untagged" });
    } catch (error) {
      console.error("Failed to remove conversation tag:", error);
    }
  };

  const toggleTagging = (conversationId) => {
    setTaggingConversationId(taggingConversationId === conversationId ? null : conversationId);
  };

  // Helper functions for tags
  const normalizeTag = (tag) => {
    if (!tag) return "";
    return tag.toLowerCase().trim();
  };

  const displayTag = (tag) => {
    if (!tag) return "";
    return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
  };

  // Fetch project details to check AI capability
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        if (!projectId) return;
        setProjectLoading(true);
        const response = await getProjectDetails(projectId);
        const data = response?.data || response;
        if (!on) return;
        setProjectDetails(data);
      } catch (error) {
        console.error("Failed to load project details:", error);
      } finally {
        if (on) setProjectLoading(false);
      }
    })();
    return () => { on = false; };
  }, [projectId]);

  // AI Analysis Handler Functions
  const proceedWithSingleAnalysis = async () => {
    if (!selected || !aiEnabled) return;
    
    setAiLoading(true);
    setShowAiSuggestion(false);
    
    try {
      const response = await analyzeConversationTag(selected.id, projectId);
      const data = response?.data || response;
      
      if (data.tag) {
        setAiSuggestion({
          tag: data.tag,
          confidence: data.confidence,
          reasoning: data.reasoning,
          cost_cents: data.cost_cents,
          is_cached: data.is_cached
        });
        setShowAiSuggestion(true);

        setTranscripts(prev => prev.map(t => {
          if (t.id === selected.id && t.convo) {
            return {
              ...t,
              convo: {
                ...t.convo,
                ai_tag_suggestion: data.tag,
                ai_tag_confidence: data.confidence,
                ai_tag_reasoning: data.reasoning,
                ai_analyzed_at: data.analyzed_at || new Date(),
                ai_tokens_used: data.tokens_used || 0,
                ai_cost_cents: data.cost_cents || 0
              }
            };
          }
          return t;
        }));
      }
    } catch (error) {
      console.error("AI analysis failed:", error);
      showNotif('error', 'AI Analysis Failed', error.message || 'Unable to analyze conversation.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAiTag = async () => {
    if (!aiSuggestion || !selected) return;
    const tagFormatted = normalizeTag(aiSuggestion.tag);
    await handleAddTag(selected.id, tagFormatted);
    setShowAiSuggestion(false);
    setAiSuggestion(null);
  };

  const proceedWithBatchAnalysis = async () => {
    if (!aiEnabled) return;

    const unanalyzedConversations = visible.filter(c => {
      const rawConvo = transcripts.find(t => t.id === c.id);
      const hasAiAnalysis = rawConvo?.convo?.ai_tag_suggestion && rawConvo?.convo?.ai_analyzed_at;
      const hasTag = conversationTags[c.id];
      return !hasAiAnalysis && !hasTag;
    });
    
    const conversationIds = unanalyzedConversations.map(c => c.id);
    
    if (conversationIds.length === 0) {
      showNotif('info', 'Nothing to Analyze', 'All visible conversations are already analyzed or manually tagged.');
      return;
    }

    setBatchAnalyzing(true);
    setShowBatchProgress(true);
    setBatchProgress({ current: 0, total: conversationIds.length });

    try {
      const response = await batchAnalyzeConversations(conversationIds, projectId);
      const data = response?.data || response;
      
      if (data.results && Array.isArray(data.results)) {
        const newTags = {};
        const aiAnalysisUpdates = {};
        
        data.results.forEach(result => {
          if (result.success && result.tag) {
            newTags[result.conversation_id] = normalizeTag(result.tag);
            aiAnalysisUpdates[result.conversation_id] = {
              ai_tag_suggestion: result.tag,
              ai_tag_confidence: result.confidence,
              ai_tag_reasoning: result.reasoning,
              ai_analyzed_at: result.analyzed_at || new Date(),
              ai_tokens_used: result.tokens_used || 0,
              ai_cost_cents: result.cost_cents || 0
            };
          }
        });
        
        setTranscripts(prev => prev.map(t => {
          if (aiAnalysisUpdates[t.id] && t.convo) {
            return {
              ...t,
              convo: {
                ...t.convo,
                ...aiAnalysisUpdates[t.id]
              }
            };
          }
          return t;
        }));
        
        // Human-tag-wins (Voice Intelligence Phase 7): never overwrite a
        // conversation that already has a human tag. The unanalyzedConversations
        // filter above already skips tagged rows, but this guard makes the rule
        // explicit + robust so a future change to that filter can't silently
        // let an AI tag clobber a human value.
        for (const [convId, tag] of Object.entries(newTags)) {
          if (conversationTags[convId]) continue;   // human tag wins
          await handleAddTag(convId, tag);
        }
        
        const successCount = data.summary.succeeded;
        const cachedCount = data.summary.cached || 0;
        const newlyAnalyzed = successCount - cachedCount;
        
        const summaryMessage = `Successfully processed ${successCount} conversation${successCount !== 1 ? 's' : ''}:\n\n` +
          `✨ Newly analyzed: ${newlyAnalyzed}\n` +
          `💾 Already cached: ${cachedCount}\n` +
          `❌ Failed: ${data.summary.failed}\n` +
          `💰 Cost: $${data.summary.total_cost_dollars}`;
        
        showNotif('success', 'Batch Analysis Complete', summaryMessage);
      }
    } catch (error) {
      console.error("Batch analysis failed:", error);
      showNotif('error', 'Batch Analysis Failed', error.message || 'Unable to complete batch analysis.');
    } finally {
      setBatchAnalyzing(false);
      setShowBatchProgress(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selected || !aiEnabled) return;
    
    setAiSummaryLoading(true);
    setShowAiSummary(false);
    
    try {
      const response = await generateAiSummary(selected.id, projectId);
      const data = response?.data || response;
      
      if (data.summary) {
        setAiSummary({
          ...data.summary,
          cost_cents: data.cost_cents,
          is_cached: data.is_cached
        });
        setShowAiSummary(true);

        setTranscripts(prev => prev.map(t => {
          if (t.id === selected.id && t.convo) {
            return {
              ...t,
              convo: {
                ...t.convo,
                ai_summary: data.summary,
                ai_analyzed_at: data.analyzed_at || new Date(),
                ai_tokens_used: (t.convo.ai_tokens_used || 0) + (data.tokens_used || 0),
                ai_cost_cents: (t.convo.ai_cost_cents || 0) + (data.cost_cents || 0)
              }
            };
          }
          return t;
        }));
      }
    } catch (error) {
      console.error("AI summary failed:", error);
      showNotif('error', 'Summary Generation Failed', error.message || 'Unable to generate AI summary.');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  // Load existing AI analysis when a conversation is selected
  useEffect(() => {
    if (!selected || !aiEnabled) {
      setAiSuggestion(null);
      setAiSummary(null);
      setShowAiSuggestion(false);
      setShowAiSummary(false);
      return;
    }

    const rawConvo = transcripts.find(t => t.id === selected.id);
    if (rawConvo?.convo) {
      if (rawConvo.convo.ai_tag_suggestion && rawConvo.convo.ai_analyzed_at) {
        setAiSuggestion({
          tag: rawConvo.convo.ai_tag_suggestion,
          confidence: parseFloat(rawConvo.convo.ai_tag_confidence) || 0,
          reasoning: rawConvo.convo.ai_tag_reasoning || '',
          cost_cents: rawConvo.convo.ai_cost_cents || 0,
          is_cached: true
        });
      } else {
        setAiSuggestion(null);
      }

      if (rawConvo.convo.ai_summary && rawConvo.convo.ai_analyzed_at) {
        setAiSummary({
          ...rawConvo.convo.ai_summary,
          cost_cents: rawConvo.convo.ai_cost_cents || 0,
          is_cached: true
        });
      } else {
        setAiSummary(null);
      }
    }
  }, [selected, transcripts, aiEnabled]);

  // Helper function to calculate days between two dates
  const getDaysDifference = (start, end) => {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    return Math.ceil((endMs - startMs) / (1000 * 60 * 60 * 24));
  };

  // Helper function to split date range into monthly chunks
  const splitDateRange = (start, end) => {
    const chunks = [];
    let currentStart = new Date(start);
    const finalEnd = new Date(end);

    while (currentStart < finalEnd) {
      const currentEnd = new Date(currentStart);
      currentEnd.setMonth(currentEnd.getMonth() + 1);
      
      // Don't exceed the final end date
      if (currentEnd > finalEnd) {
        currentEnd.setTime(finalEnd.getTime());
      }

      chunks.push({
        start: formatYYYYMMDD(currentStart),
        end: formatYYYYMMDD(currentEnd)
      });

      // Move to next month
      currentStart = new Date(currentEnd);
      currentStart.setDate(currentStart.getDate() + 1);
    }

    return chunks;
  };

  // Convert one raw API conversation into the row shape used by the conversation list.
  //   Post-Supabase shape uses c.id / c.started_at / c.visitor_name / c.duration_seconds /
  //   c.message_count. Legacy shape (c.conversationID / c.customer_name / c.createdAt) is
  //   preserved as fallback for any remaining pre-migration callers.
  const mapConversationToRow = (c, idx) => {
    try {
      const msgs = normalizeMessages(c);
      const firstMsg = msgs[0];
      const lastMsg = msgs[msgs.length - 1];
      const ts = c.started_at || c.createdAt || c.created_at || getMsgTime(firstMsg) || null;
      const parsed = tryParseDate(ts);
      const createdAt = parsed ? parsed.getTime() : 0;

      // Prefer the server-provided duration (conversations.duration_seconds).
      // Fall back to computing from first/last message time when messages are
      // present (they usually aren't in the list-level fetch).
      let durationMs = null;
      let durationFormatted = null;
      if (c.duration_seconds != null && Number.isFinite(Number(c.duration_seconds))) {
        durationMs = Number(c.duration_seconds) * 1000;
        durationFormatted = formatDelta(durationMs);
      } else {
        const firstMsgTime = getMsgTime(firstMsg);
        const lastMsgTime = getMsgTime(lastMsg);
        if (firstMsgTime && lastMsgTime && msgs.length > 1) {
          const firstParsed = tryParseDate(firstMsgTime);
          const lastParsed = tryParseDate(lastMsgTime);
          if (firstParsed && lastParsed) {
            durationMs = lastParsed.getTime() - firstParsed.getTime();
            durationFormatted = formatDelta(durationMs);
          }
        }
      }

      const id = c.id || c.conversationID || String(idx);
      const rawName = c?.visitor_name || c?.visitor_email || c?.visitor_phone
        || c?.customer_name || c?.user?.name || c?.visitor?.name || c?.name;
      const name = rawName && String(rawName).startsWith('+') ? formatPhoneNumber(rawName) : rawName;
      const displayName = displayNameFor(name, id);
      const rawConcat = msgs.map(getMsgText).join(" \n").slice(0, 50000);

      return {
        id,
        displayName,
        date: parsed ? formatYYYYMMDD(parsed) : "",
        time: parsed ? parsed.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "",
        createdAt,
        convo: c,
        _msgs: msgs,
        _searchRaw: rawConcat,
        _searchIdx: rawConcat.toLowerCase(),
        tag: c.tag,
        durationMs,
        durationFormatted,
        messageCount: (c.message_count != null ? Number(c.message_count) : msgs.length),
      };
    } catch (err) {
      console.error("Error processing conversation:", err, c);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) return;

      setTranscriptsLoading(true);
      setTranscriptsError("");
      setSelectedId(null);
      setTranscripts([]);
      setConversationTags({});
      setTotalConversations(0);
      setLoadingProgress(null);
      setLoadingMore(false);

      let offset = 0;
      let total = 0;
      let isFirstBatch = true;

      try {
        while (true) {
          if (cancelled) return;

          const resp = await getTranscripts(projectId, startDate, endDate, forceRefresh, {
            offset,
            limit: TRANSCRIPT_PAGE_SIZE,
            modality: modalityFilter,
          });
          const root = resp?.data?.data ?? resp?.data ?? resp ?? {};
          // getTranscripts returns { transcripts, total, offset, limit }.
          // Older/legacy shape used `conversations` — accept both so a partial
          // rollout on the API side doesn't break the list. Bug: previously
          // only read `conversations`, so 155 total rendered but 0 items showed.
          const convs = root?.transcripts ?? root?.conversations ?? [];
          if (cancelled) return;

          if (isFirstBatch) {
            total = Number.isFinite(root.total) ? root.total : convs.length;
            setTotalConversations(total);
            isFirstBatch = false;
            // First batch arrived — flip "initial load" off and switch to background-load mode.
            setTranscriptsLoading(false);
            if (forceRefresh) setForceRefresh(false);
          }

          const newRows = convs.map(mapConversationToRow).filter(r => r !== null);
          if (!cancelled && newRows.length) {
            setTranscripts(prev => prev.concat(newRows));
            setConversationTags(prev => {
              const next = { ...prev };
              for (const row of newRows) {
                if (row.tag && row.tag !== "untagged") next[row.id] = row.tag;
              }
              return next;
            });
          }

          offset += TRANSCRIPT_PAGE_SIZE;
          // 2026-08-07 — getTranscripts (Supabase direct) never returned the
          // legacy `hasMore` key, so `root.hasMore && …` was always falsy and
          // the loop stopped after the first 10 rows while the footer spinner
          // (gated on transcripts.length < total) ran forever — the reported
          // "infinite loading". Derive continuation from the exact-count total;
          // the empty-batch guard prevents a runaway loop if rows disappear
          // mid-pagination.
          const hasMore = convs.length > 0 && offset < total;
          if (!hasMore) break;

          setLoadingMore(true);
          setLoadingProgress({ current: Math.min(offset, total), total });
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404 || e?.error === 'No transcripts found for this project.') {
          setTranscripts([]);
          setConversationTags({});
          setTotalConversations(0);
          setTranscriptsError("");
        } else {
          setTranscriptsError(e?.response?.data?.message || e?.message || "Failed to load transcripts.");
        }
      } finally {
        if (!cancelled) {
          setTranscriptsLoading(false);
          setLoadingMore(false);
          setLoadingProgress(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, startDate, endDate, forceRefresh, modalityFilter]);

  const sortedTranscripts = useMemo(() => {
    const copy = [...transcripts];
    
    if (sortBy === "activity") {
      copy.sort((a, b) => b.messageCount - a.messageCount);
    } else {
      const dir = sortOrder === "asc" ? 1 : -1;
      copy.sort((a, b) => (a.createdAt - b.createdAt) * dir);
    }
    
    return copy;
  }, [transcripts, sortOrder, sortBy]);

  useEffect(() => {
    if (!selectedId && sortedTranscripts.length > 0) {
      setSelectedId(sortedTranscripts[0].id);
    }
  }, [sortedTranscripts, selectedId]);

  // First, apply date filters to get the base count (before other filters)

  // Phase 7 — merge fn_get_conversation_list onto the loaded rows. The list keeps
  // loading through getTranscripts so search, Tags, sort, Clear, CSV, PDF and the
  // total counter keep working over the full in-memory set; this only adds the
  // intelligence fields that loader cannot see.
  //
  // `ready` sequences it AFTER the list has loaded rather than firing it on
  // mount. The page already opens ~40 requests at once — the analysis band's
  // batch, the list's own paging, profile and feature lookups — and adding this
  // paging loop to that burst pushed it past what the server will accept
  // concurrently: requests came back ERR_HTTP2_SERVER_REFUSED_STREAM and whole
  // panels rendered "No data for this period" while their data existed.
  // Enrichment is additive, so it can wait for a quiet moment; the rows it
  // decorates must exist first anyway.
  // Switching modality is a query-string change on the SAME route, so this
  // component never remounts and the intelligence filters persist across it.
  // That turns two of them into hidden predicates:
  //
  //   After hours, set on voice, keeps filtering on chat — where the control is
  //   not mounted at all, so the list is narrowed by something the reader cannot
  //   see or clear. conversationIntel.js's own header forbids exactly that.
  //
  //   Outcome and stay type desync: "Voicemail left" is not in the chat
  //   vocabulary, so the <select> falls back to displaying "All" while
  //   applyIntelFilters still filters on it — an empty list under a control
  //   claiming nothing is filtered.
  //
  // Resetting on the switch is the honest default: a filter set for one channel
  // was never a statement about the other.
  useEffect(() => {
    setIntelFilters(DEFAULT_INTEL_FILTERS);
    clearPanelFilter();
  }, [modalityFilter, clearPanelFilter]);

  const intelMap = useIntelEnrichment({
    clientId: projectId,
    modality: isIntelligence ? modalityFilter : null,
    startDate,
    endDate,
    ready: !transcriptsLoading && transcripts.length > 0,
  });

  // Sort and filter pills: the touch floor and the darker selected fill are part
  // of the analysis surface (view=intelligence). The plain transcript list keeps
  // the original geometry and the original brand-blue chip.
  const PILL_FLOOR = isIntelligence ? "max-md:min-h-[44px] " : "";
  const PILL_ON = isIntelligence
    ? "bg-[#2175A8] text-white border-[#2175A8]"
    : "bg-[#32B1FF] text-white border-[#32B1FF]";
  // Whether an analysis band renders above the master-detail grid. The band
  // mount and the grid's height read the SAME flag, because a grid keeping a
  // viewport-derived height under a 5,000px band is precisely the bug that was
  // fixed. On this base the gate also decides it, so the two cannot disagree.
  const hasAnalysisBand = isIntelligence;

  const dateFiltered = useMemo(() => {
    let filtered = sortedTranscripts;

    // Apply target date filter (for Today/Yesterday buttons with timezone buffer)
    if (targetDateFilter) {
      filtered = filtered.filter((row) => {
        return row.date === targetDateFilter;
      });
    } else {
      // Apply date range filter (frontend safety check)
      if (startDate || endDate) {
        const beforeFilter = filtered.length;
        filtered = filtered.filter((row) => {
          if (!row.date) return false; // Exclude conversations without dates
          
          const rowDate = row.date; // Format: YYYY-MM-DD
          
          // Check if within start date
          if (startDate && rowDate < startDate) return false;
          
          // Check if within end date
          if (endDate && rowDate > endDate) return false;
          
          return true;
        });
        const afterFilter = filtered.length;
        if (beforeFilter !== afterFilter) {
          console.log(`🔍 Frontend date filter: Removed ${beforeFilter - afterFilter} conversations outside range (${startDate} to ${endDate})`);
        }
      }
    }

    // Modality filter (chat/voice) composes with all downstream filters
    // (tags/search/sort). row.convo carries the raw conversations row.
    if (modalityFilter) {
      filtered = filtered.filter((row) => {
        const m = row?.convo?.modality;
        return typeof m === "string" && m.toLowerCase() === modalityFilter;
      });
    }

    return filtered;
  }, [sortedTranscripts, targetDateFilter, startDate, endDate, modalityFilter]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let filtered = dateFiltered;

    // Apply tag filter if selected
    if (selectedTagFilter) {
      filtered = filtered.filter((row) => {
        const tag = conversationTags[row.id];
        return tag && tag.toLowerCase() === selectedTagFilter.toLowerCase();
      });
    }

    // Apply message length filter
    if (messageLengthFilter !== "all") {
      filtered = filtered.filter((row) => {
        const count = row.messageCount;
        if (messageLengthFilter === "short") return count >= 1 && count <= 5;
        if (messageLengthFilter === "medium") return count >= 6 && count <= 15;
        if (messageLengthFilter === "long") return count >= 16;
        return true;
      });
    }

    // Apply time of day filter
    if (timeOfDayFilter !== "all") {
      filtered = filtered.filter((row) => {
        const parsed = tryParseDate(row.convo?.createdAt || getMsgTime(row._msgs[0]));
        if (!parsed) return false;
        const hour = parsed.getHours();
        
        if (timeOfDayFilter === "morning") return hour >= 6 && hour < 12;
        if (timeOfDayFilter === "afternoon") return hour >= 12 && hour < 18;
        if (timeOfDayFilter === "evening") return hour >= 18 && hour < 24;
        if (timeOfDayFilter === "night") return hour >= 0 && hour < 6;
        return true;
      });
    }

    // Apply visitor type filter
    if (visitorTypeFilter !== "all") {
      filtered = filtered.filter((row) => {
        const isGuest = row.displayName.startsWith("Guest ");
        if (visitorTypeFilter === "named") return !isGuest;
        if (visitorTypeFilter === "guest") return isGuest;
        return true;
      });
    }

    // Apply AI analysis filter (only if AI is enabled)
    if (aiEnabled && aiAnalysisFilter !== "all") {
      filtered = filtered.filter((row) => {
        const rawConvo = transcripts.find(t => t.id === row.id);
        const hasAiTag = rawConvo?.convo?.ai_tag_suggestion && rawConvo?.convo?.ai_analyzed_at;
        const hasAiSummary = rawConvo?.convo?.ai_summary && rawConvo?.convo?.ai_analyzed_at;
        
        if (aiAnalysisFilter === "tagged") return hasAiTag;
        if (aiAnalysisFilter === "summarized") return hasAiSummary;
        if (aiAnalysisFilter === "both") return hasAiTag && hasAiSummary;
        if (aiAnalysisFilter === "none") return !hasAiTag && !hasAiSummary;
        return true;
      });
    }

    // Attach the intelligence record before filtering. A row the list RPC did
    // not return simply has no `intel` — it is never dropped for that reason.
    if (Object.keys(intelMap).length > 0) {
      if (isIntelligence) filtered = filtered.map((row) => mergeIntel(row, intelMap[row.id]));
    }

    // Phase 7 — Voice Intelligence filters. Every field they read comes from
    // fn_get_conversation_list, merged onto the row as `intel` above; the
    // predicate itself lives in components/conversation/list so both pages and
    // its tests share one implementation.
    if (isIntelligence) filtered = applyIntelFilters(filtered, intelFilters);

    // A filter raised by a panel in the analysis band. Its own predicate, not a
    // rewrite of the search box, so it composes with whatever a reader typed.
    filtered = applyPanelFilterTo(filtered, panelFilter);

    // Apply search query filter
    if (!q) return filtered;

    const terms = q.split(/\s+/).filter(Boolean);
    const matchesAllTerms = (row) =>
      terms.every(
        (t) =>
          (row.displayName || "").toLowerCase().includes(t) ||
          (row.date || "").toLowerCase().includes(t) ||
          (row._searchIdx || "").includes(t)
      );

    return filtered
      .filter(matchesAllTerms)
      .map((row) => {
        let snippet = null;
        for (const t of terms) {
          if (t && row._searchIdx && row._searchIdx.includes(t)) {
            snippet = makeSnippet(row._searchIdx, row._searchRaw, t);
            if (snippet) break;
          }
        }
        return { ...row, _snippet: snippet };
      });
  }, [dateFiltered, query, selectedTagFilter, conversationTags, messageLengthFilter, timeOfDayFilter, visitorTypeFilter, aiAnalysisFilter, transcripts, aiEnabled, intelFilters, intelMap, panelFilter]);

  // Calculate unanalyzed conversations count (for batch button)
  const unanalyzedCount = useMemo(() => {
    if (!aiEnabled) return 0;
    return visible.filter(c => {
      const rawConvo = transcripts.find(t => t.id === c.id);
      const hasAiAnalysis = rawConvo?.convo?.ai_tag_suggestion && rawConvo?.convo?.ai_analyzed_at;
      const hasTag = conversationTags[c.id];
      return !hasAiAnalysis && !hasTag;
    }).length;
  }, [visible, transcripts, conversationTags, aiEnabled]);

  const username = localStorage.getItem("username") || "admin";
  // Clicking Date has to be able to LEAVE activity sort. Previously it only
  // flipped asc/desc, so once Activity was selected the Date button toggled the
  // arrow forever and never took the sort back — it looked permanently dead.
  // First click returns to date sort; clicking it again reverses the order.
  const onSortDate = () => {
    if (isIntelligence && sortBy !== "date") { setSortBy("date"); return; }
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };
  const sortArrow = sortOrder === "asc" ? " ▲" : " ▼";

  // Clear all filters
  const clearAllFilters = () => {
    setQuery("");
    setSelectedTagFilter(null);
    setMessageLengthFilter("all");
    setTimeOfDayFilter("all");
    setVisitorTypeFilter("all");
    setSortBy("date");
    setAiAnalysisFilter("all");
    setIntelFilters(DEFAULT_INTEL_FILTERS);
    setTargetDateFilter(null);
    clearPanelFilter();
    // `setActiveQuickFilter` used to be called here. The quick-filter state it
    // belonged to is gone, so the call was a reference to an undeclared binding
    // and every click on Clear threw ReferenceError before reaching the end of
    // the function — meaning the filters it had already reset were the only ones
    // that cleared. Present on origin/develop, not introduced by this feature.
  };

  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = messageLengthFilter !== "all" || timeOfDayFilter !== "all" || visitorTypeFilter !== "all" || aiAnalysisFilter !== "all";

  /* ----------------------- EXPORT HELPERS ----------------------- */
  const formatIsoDateTime = (ts) => {
    const d = tryParseDate(ts);
    if (!d) return "";
    return `${d.toISOString().slice(0, 10)} ${d.toTimeString().slice(0, 8)}`;
  };

  const exportSelectedAsCSV = () => {
    if (!selected) return;
    // Prefer messages fetched from conversation_messages; fall back to any
    // legacy shape the router state may have carried on the raw row.
    const msgs = (selectedMessages && selectedMessages.length > 0) ? selectedMessages : normalizeMessages(selected.convo);
    
    const convoTs = selected.convo?.createdAt || getMsgTime(msgs[0]);
    const baseDate = convoTs ? tryParseDate(convoTs) : null;
    
    const rows = [
      // Uppercase, no underscores — a spreadsheet header is read by a person,
      // not by the database it came from.
      isIntelligence ? ["conversation_id", "participant", "message"].map(upperLabel) : ["conversation_id", "participant", "message"],
      ...msgs.map((m, idx) => {
        let msgText = getMsgText(m);
        msgText = msgText.replace(/\n/g, " ").replace(/"/g, "'").trim();
        
        return [
          selected.id,
          getMsgSide(m) === "right" ? "human" : "bot",
          msgText,
        ];
      }),
    ];
    const csv = Papa.unparse(rows);
    // UTF-8 BOM: Excel needs it to read the file as UTF-8 and — since the first header is
    // "ID" — to not mistake the leading "ID" for SYLK format and refuse to open the file.
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    // The file name becomes the sheet title, so it follows the same rule as the
    // headers: spaces rather than underscores, uppercased.
    const a = document.createElement("a");
    a.href = url;
    a.download = `${upperLabel(`conversation ${selected.displayName || "guest"} ${selected.date || "unknown"}`)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Defer the revoke: a synchronous revoke truncates the download into an unopenable,
    // UUID-named file before the browser has finished reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const exportSelectedAsPDF = async () => {
    if (!selected) return;
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });

    const pageWidth = doc.internal.pageSize.width;
    const logoWidth = 60;
    const logoHeight = 40;
    const marginRight = 60;
    const marginLeft = 50;
    
    let logoLoaded = false;
    if (projectLogo) {
      try {
        const response = await fetch(projectLogo);
        const blob = await response.blob();
        
        const logoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        const logoX = pageWidth - logoWidth - marginRight;
        doc.addImage(logoDataUrl, 'PNG', logoX, 35, logoWidth, logoHeight);
        logoLoaded = true;
      } catch (e) {
        console.error("Could not load project logo:", e);
      }
    }

    // Prefer messages fetched from conversation_messages; fall back to any
    // legacy shape the router state may have carried on the raw row.
    const msgs = (selectedMessages && selectedMessages.length > 0) ? selectedMessages : normalizeMessages(selected.convo);
    
    const convoTs = selected.convo?.createdAt || getMsgTime(msgs[0]);
    const baseDate = convoTs ? tryParseDate(convoTs) : null;
    
    const textMaxWidth = logoLoaded ? (pageWidth - marginLeft - 30 - logoWidth - marginRight) : (pageWidth - marginLeft - marginRight);
    
    let yPos = 40;
    
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(projectName || "Project Report", marginLeft, yPos, { maxWidth: textMaxWidth });
    
    yPos += 18;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}`, marginLeft, yPos);
    
    yPos += 12;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(1);
    doc.line(marginLeft, yPos, pageWidth - marginRight, yPos);
    
    yPos += 20;
    const conversationTitle = `Conversation — ${selected.displayName}`;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(conversationTitle, marginLeft, yPos, { maxWidth: textMaxWidth });
    
    yPos += 18;
    const conversationDate = `Date: ${selected.date || "unknown"}${selected.time ? ` at ${selected.time}` : ""}`;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(conversationDate, marginLeft, yPos, { maxWidth: textMaxWidth });
    doc.setTextColor(0, 0, 0);
    
    const tag = conversationTags[selected.id];
    if (tag && tag.toLowerCase() !== 'untagged') {
      yPos += 15;
      const tagLower = tag.toLowerCase();
      let bgRgb, textRgb;
      
      if (tagLower === 'info_seeking') {
        bgRgb = [221, 214, 254]; // bg-purple-200
        textRgb = [107, 33, 168];  // text-purple-800
      } else if (tagLower === 'availability_search') {
        bgRgb = [191, 219, 254]; // bg-blue-200
        textRgb = [30, 64, 175];   // text-blue-800
      } else if (tagLower === 'ready_to_book') {
        bgRgb = [187, 247, 208]; // bg-green-200
        textRgb = [22, 101, 52];   // text-green-800
      } else if (tagLower === 'post_booking_support') {
        bgRgb = [253, 230, 138]; // bg-amber-200
        textRgb = [146, 64, 14];   // text-amber-800
      } else if (tagLower === 'spam_other') {
        bgRgb = [254, 202, 202]; // bg-red-200
        textRgb = [153, 27, 27];   // text-red-800
      } else {
        bgRgb = [229, 231, 235];
        textRgb = [55, 65, 81];
      }
      
      const tagDisplay = capitalizeTag(tag);
      // Size the pill to its text. A fixed width clipped any longer tag:
      // "Post Booking Support" is wider than the box, and because the text is
      // centred it spilled out of BOTH ends of the rounded rect. Measure first —
      // getTextWidth only reports the right number once the font is set.
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      const tagTextWidth = doc.getTextWidth(tagDisplay);
      const tagPadX = 8;
      const tagWidth = tagTextWidth + tagPadX * 2;

      doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
      doc.roundedRect(marginLeft, yPos - 8, tagWidth, 16, 4, 4, 'FD');
      doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
      doc.text(tagDisplay, marginLeft + tagPadX, yPos + 3);
      
      yPos += 18;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
    }

    // Voice Intelligence values. The PDF renders one transcript table per
    // conversation rather than one row per conversation, so there is no column
    // to add — this is the header-block equivalent. Absent values are omitted,
    // never printed blank.
    // `selected` comes from `transcripts` (the raw loaded rows), not from
    // `visible` where the intelligence record is merged — so look it up by id,
    // the same way the CSV export does.
    for (const [label, value] of (isIntelligence ? intelExportPairs({ intel: intelMap[selected.id] }) : [])) {
      yPos += 14;
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text(`${label}:`, marginLeft, yPos);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(String(value), marginLeft + 70, yPos, { maxWidth: textMaxWidth - 70 });
    }
    
    const body = msgs.map((m, idx) => {
      const timeRaw = getMsgTime(m);
      let timeFormatted = "";
      
      if (timeRaw) {
        timeFormatted = formatTimeOnlySafe(timeRaw) || formatIsoDateTime(timeRaw);
      }
      
      if (!timeFormatted && baseDate && idx === 0) {
        timeFormatted = selected.time || "";
      } else if (!timeFormatted && baseDate) {
        const estTime = new Date(baseDate.getTime() + (idx * 30000));
        timeFormatted = estTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
      }
      
      if (!timeFormatted) {
        timeFormatted = "—";
      }
      
      let msgText = getMsgText(m) || "(no text)";
      msgText = cleanTextForPDF(msgText);
      
      return [
        idx + 1,
        getMsgSide(m) === "right" ? "Human" : "Bot",
        timeFormatted,
        msgText,
      ];
    });

    const startYPosition = yPos + 20;
    
    autoTable(doc, {
      startY: startYPosition,
      head: [["#", "Participant", "Time", "Message"]],
      body,
      styles: { 
        fontSize: 9, 
        cellPadding: 6,
        overflow: "linebreak",
        cellWidth: "wrap",
        valign: "top",
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
        font: "helvetica",
      },
      columnStyles: { 
        0: { cellWidth: 30, halign: "center", fontStyle: "bold", textColor: [100, 100, 100] }, 
        1: { cellWidth: 75, halign: "left", fontStyle: "bold" }, 
        2: { cellWidth: 75, halign: "left", textColor: [100, 100, 100] }, 
        3: { cellWidth: 'auto', halign: "left", overflow: "linebreak" } 
      },
      theme: "striped",
      headStyles: { 
        fillColor: [50, 177, 255],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        halign: "left",
      },
      margin: { left: 50, right: 60, top: 40, bottom: 40 },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 20
        );
      },
    });

    const safeName = (selected.displayName || "guest").replace(/\s+/g, "_");
    doc.save(`conversation_${safeName}_${selected.date || "unknown"}.pdf`);
  };

  const [csvExporting, setCsvExporting] = useState(false);
  // Server-side CSV download — independent of pagination state.
  const exportAllConversationsAsCsv = async () => {
    if (csvExporting) return;
    try {
      setCsvExporting(true);
      // Voice AND chat exports gain the intelligence columns — outcome, stay
      // type, urgency, barriers, value are on both modalities. The CSV is
      // generated from its own query, so the rows arriving in `cellsFor` are
      // conversation rows keyed by `id` — look the record up in the same map the
      // list uses.
      await downloadTranscriptsCsv(
        projectId, startDate, endDate, modalityFilter,
        (modalityFilter === "voice" || modalityFilter === "chat")
          ? {
              headers: INTEL_EXPORT_HEADERS,
              cellsFor: (r) => (isIntelligence ? intelExportCells({ intel: intelMap[r.id] }) : []),
            }
          : null,
      );
    } catch (err) {
      showWarning('Export Failed', err?.response?.data?.message || err?.message || 'Could not export CSV.');
    } finally {
      setCsvExporting(false);
    }
  };

  const exportAllConversationsAsPDF = async () => {
    if (visible.length === 0) {
      showWarning('Export Warning', 'No conversations available to export. Please ensure you have transcripts loaded.');
      return;
    }

    // 2026-07-29 — the list-level fetch (getTranscripts) never included
    // message bodies, so `normalizeMessages(conv.convo)` returned [] for
    // every row and the exported PDF was blank pages. Preload messages
    // in parallel batches before rendering. On orphan voice rows the
    // RPC returns [] anyway (Part 8 G3 / Part 9 §3) — those pages will
    // show a "no transcript captured" note instead of blank space.
    const BATCH_SIZE = 8;
    const messagesById = new Map();
    for (let i = 0; i < visible.length; i += BATCH_SIZE) {
      const chunk = visible.slice(i, i + BATCH_SIZE);
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.all(chunk.map(async (row) => {
        try {
          const resp = await getConversationMessages(row.id);
          return { id: row.id, msgs: Array.isArray(resp?.data) ? resp.data : [] };
        } catch {
          return { id: row.id, msgs: [] };
        }
      }));
      for (const r of results) messagesById.set(r.id, r.msgs);
    }

    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const logoWidth = 70;
    const logoHeight = 47;
    const marginRight = 60;
    const marginLeft = 50;
    
    let logoLoaded = false;
    if (projectLogo) {
      try {
        const response = await fetch(projectLogo);
        const blob = await response.blob();
        
        const logoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        const logoX = pageWidth - logoWidth - marginRight;
        doc.addImage(logoDataUrl, 'PNG', logoX, 40, logoWidth, logoHeight);
        logoLoaded = true;
      } catch (e) {
        console.error("Could not load project logo:", e);
      }
    }

    const textMaxWidth = logoLoaded ? (pageWidth - marginLeft - 20 - logoWidth - marginRight) : (pageWidth - marginLeft - marginRight);

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`${projectName || "All Conversations"} Report`, marginLeft, 55, { maxWidth: textMaxWidth });
    
    let yPos = 78;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Date Range: ${startDate} to ${endDate}`, marginLeft, yPos, { maxWidth: textMaxWidth });
    doc.text(`Total Conversations: ${visible.length}`, marginLeft, yPos + 13, { maxWidth: textMaxWidth });
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, marginLeft, yPos + 26, { maxWidth: textMaxWidth });
    doc.setTextColor(0, 0, 0);
    
    yPos = 120;

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(1);
    doc.line(marginLeft, yPos, pageWidth - marginRight, yPos);
    yPos += 25;

    for (let i = 0; i < visible.length; i++) {
      const conv = visible[i];
      // Prefer server-fetched messages (via fn_get_conversation_detail).
      // Fall back to whatever `normalizeMessages` can salvage from the raw
      // conversation row so mocked/legacy data still renders in tests.
      const preloaded = messagesById.get(conv.id) || [];
      const msgs = preloaded.length > 0 ? preloaded : normalizeMessages(conv.convo);
      const isVoiceOrphan = conv.convo?.modality === 'voice' && !conv.convo?.phone_call_session_id;

      if (i > 0) {
        doc.addPage();
        yPos = 50;
      }

      doc.setFontSize(15);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(`Conversation ${i + 1}: ${conv.displayName}`, marginLeft, yPos, { maxWidth: pageWidth - marginLeft - marginRight });
      
      yPos += 20;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${conv.date} at ${conv.time}`, marginLeft, yPos);
      doc.setTextColor(0, 0, 0);
      
      const tag = conversationTags[conv.id];
      if (tag && tag.toLowerCase() !== 'untagged') {
        yPos += 18;
        const tagLower = tag.toLowerCase();
        let bgRgb, textRgb;
        
        if (tagLower === 'info_seeking') {
          bgRgb = [221, 214, 254]; // bg-purple-200
          textRgb = [107, 33, 168];  // text-purple-800
        } else if (tagLower === 'availability_search') {
          bgRgb = [191, 219, 254]; // bg-blue-200
          textRgb = [30, 64, 175];   // text-blue-800
        } else if (tagLower === 'ready_to_book') {
          bgRgb = [187, 247, 208]; // bg-green-200
          textRgb = [22, 101, 52];   // text-green-800
        } else if (tagLower === 'post_booking_support') {
          bgRgb = [253, 230, 138]; // bg-amber-200
          textRgb = [146, 64, 14];   // text-amber-800
        } else if (tagLower === 'spam_other') {
          bgRgb = [254, 202, 202]; // bg-red-200
          textRgb = [153, 27, 27];   // text-red-800
        } else {
          bgRgb = [229, 231, 235];
          textRgb = [55, 65, 81];
        }
        
        const tagDisplay = capitalizeTag(tag);
        // Size the pill to its text. A fixed width clipped any longer tag:
        // "Post Booking Support" is wider than the box, and because the text is
        // centred it spilled out of BOTH ends of the rounded rect. Measure first —
        // getTextWidth only reports the right number once the font is set.
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const tagTextWidth = doc.getTextWidth(tagDisplay);
        const tagPadX = 8;
        const tagWidth = tagTextWidth + tagPadX * 2;

        doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
        doc.roundedRect(marginLeft, yPos - 8, tagWidth, 16, 4, 4, 'FD');
        doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
        doc.text(tagDisplay, marginLeft + tagPadX, yPos + 3);
        
        yPos += 18;
      }

      // Same intelligence values as the single-conversation export.
      for (const [label, value] of (isIntelligence ? intelExportPairs(conv) : [])) {
        yPos += 14;
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(`${label}:`, marginLeft, yPos);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(String(value), marginLeft + 70, yPos, { maxWidth: pageWidth - marginLeft - marginRight - 70 });
      }
      
      yPos += 10;

      // No messages for this conversation — render a note instead of a
      // blank auto-table so the PDF is honest about what's missing.
      if (msgs.length === 0) {
        doc.setFontSize(11);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(120, 120, 120);
        const note = isVoiceOrphan
          ? "Transcript pending. The voice call was received; its transcript is still being processed or recovered by the automated backfill."
          : "No transcript available for this conversation.";
        doc.text(note, marginLeft, yPos + 10, { maxWidth: pageWidth - marginLeft - marginRight });
        yPos += 40;
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        continue;
      }

      const body = msgs.map((m, idx) => {
        const timeRaw = getMsgTime(m);
        let timeFormatted = "";

        if (timeRaw) {
          timeFormatted = formatTimeOnlySafe(timeRaw);
        }

        if (!timeFormatted) {
          const baseDate = tryParseDate(conv.convo?.createdAt || getMsgTime(msgs[0]));
          if (baseDate && idx === 0) {
            timeFormatted = conv.time || "";
          } else if (baseDate) {
            const estTime = new Date(baseDate.getTime() + (idx * 30000));
            timeFormatted = estTime.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
          } else {
            timeFormatted = "—";
          }
        }

        let msgText = getMsgText(m) || "(no text)";
        msgText = cleanTextForPDF(msgText);

        return [
          idx + 1,
          getMsgSide(m) === "right" ? "Human" : "Bot",
          timeFormatted,
          msgText,
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [["#", "Participant", "Time", "Message"]],
        body,
        styles: { 
          fontSize: 9, 
          cellPadding: 6,
          overflow: "linebreak",
          cellWidth: "wrap",
          valign: "top",
          lineColor: [220, 220, 220],
          lineWidth: 0.5,
          font: "helvetica",
        },
        columnStyles: { 
          0: { cellWidth: 30, halign: "center", fontStyle: "bold", textColor: [100, 100, 100] }, 
          1: { cellWidth: 75, halign: "left", fontStyle: "bold" }, 
          2: { cellWidth: 75, halign: "left", textColor: [100, 100, 100] }, 
          3: { cellWidth: 'auto', halign: "left", overflow: "linebreak" } 
        },
        theme: "striped",
        headStyles: { 
          fillColor: [50, 177, 255],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: "bold",
          halign: "left",
        },
        margin: { left: marginLeft, right: marginRight, top: 40, bottom: 40 },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount} - Conversation ${i + 1} of ${visible.length}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 20
          );
        },
      });

      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : yPos + 200;
    }

    const safeName = (projectName || "all_conversations").replace(/\s+/g, "_");
    doc.save(isIntelligence
      ? `${transcriptsExportTitle(projectId, startDate, endDate, modalityFilter)}.pdf`
      : `all_conversations_${startDate}_to_${endDate}.pdf`);
  };

  // THE RAIL — the conversation list. Declared here, rendered by whichever
  // layout branch is in play below, so the two branches can never render
  // different lists. `listTopRef` stays on it for usePanelFilter.
  const conversationRail = (
          <aside
            ref={listTopRef}
            className={hasAnalysisBand
              ? "flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-900"
              : "md:col-span-5 lg:col-span-4 border-r dark:border-gray-800 flex flex-col min-h-0 bg-white dark:bg-gray-900"}
          >
            {/* ABOVE THE ROWS, AS ONE UNIT — the counter, the exports, search, the
                sorts AND the filters. Two sibling divs in one wrapper: split
                apart they could scroll independently of each other.

                IT CARRIES NO `md:sticky` ANY MORE, AND THAT IS THE POINT.
                It used to, from the band-above-grid layout where this whole
                block scrolled away with the document and sticky was the only
                thing holding it. Under the §2.1 shell the rail is its own
                bounded region and the rows scroll in a port BELOW this wrapper —
                so this wrapper has no scrolling ancestor at all, and
                `position: sticky` with nothing to scroll against never engages.
                Verified in a live browser: zero scrolling ancestors. It was dead
                CSS, and the two E2E assertions that "proved" it pinned
                (offset === 0, search on screen) were structurally guaranteed by
                the flex layout and passed with the classes deleted. Both went
                with it. The flex layout gives the pinning for free.

                The opaque background stays. It is belt-and-braces now that
                nothing passes underneath, but it costs nothing and removing it
                is a visual change this stage has no reason to make.

                WHAT IT COSTS, measured on voice under the old layout: 402px,
                31% of a 1920x1080 viewport leaving seven rows, 51% of a 1280x800
                one leaving about two. The rail is now a fixed 320–360px rather
                than `md:col-span-5`, so re-measure before quoting these. */}
            <div data-testid="rail-header" className="bg-white dark:bg-gray-900">
            <div className="px-3 py-3 border-b dark:border-gray-800 space-y-3">
              {/* Same reason as the filter row below: the title and the two
                  export buttons overflowed a 311px rail at 200% text. */}
              <div className="flex flex-wrap items-center justify-between gap-y-1">
                <div className="text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase">
                  Conversations
                  {totalConversations > 0 && (
                    <span className={`ml-2 normal-case font-normal ${isIntelligence ? "text-gray-500 dark:text-gray-400" : "text-gray-400 dark:text-gray-500"}`}>
                      {loadingMore
                        ? `Loaded ${transcripts.length} of ${totalConversations}…`
                        : `${totalConversations} total`}
                    </span>
                  )}
                </div>
                {(sortedTranscripts.length > 0 || loadingMore || totalConversations > 0) && (
                  <div className={isIntelligence ? "flex items-center flex-wrap gap-1.5" : "flex items-center gap-1.5"}>
                    {/* CSV: server-side export, always available (works regardless of pagination). */}
                    <button
                      type="button"
                      onClick={exportAllConversationsAsCsv}
                      disabled={csvExporting || totalConversations === 0}
                      className={isIntelligence ? (csvExporting ? BUTTON_DISABLED : BULK_CSV_BUTTON) : LEGACY_BULK_CSV_BUTTON(csvExporting)}
                      title={csvExporting ? "Preparing CSV…" : `Download all ${totalConversations || ''} conversations as CSV`}
                    >
                      {csvExporting ? (
                        <svg className="animate-spin w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      CSV ({totalConversations})
                    </button>
                    {/* PDF: client-side, needs all rows loaded. Disabled until background load completes. */}
                    <button
                      type="button"
                      onClick={exportAllConversationsAsPDF}
                      disabled={loadingMore}
                      className={isIntelligence ? (loadingMore ? BUTTON_DISABLED : BULK_PDF_BUTTON) : LEGACY_BULK_PDF_BUTTON(loadingMore)}
                      title={loadingMore ? "Waiting for all conversations to finish loading…" : "Export filtered conversations to PDF"}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      PDF ({loadingMore ? totalConversations : visible.length})
                    </button>
                  </div>
                )}
              </div>
              {/* Date range is controlled by the global header filter — no per-page From/To inputs. */}

              <div className="relative">
                <input
                  className="w-full border dark:border-gray-700 rounded-lg pl-9 pr-9 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Search keywords…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
                />
                <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isIntelligence ? "text-gray-500 dark:text-gray-400" : "text-gray-400 dark:text-gray-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                </svg>
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full border dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Filter Controls */}
              <div className="space-y-2.5">
                {/* Sort and Actions */}
                {/* `flex-wrap`: at a 200% root font this row could not fit its
                    five controls in a 311px rail and pushed the whole list into a
                    horizontal scroll — Part 9's "200% text resize without
                    clipping". Wrapping costs nothing at 100%, where it never
                    wraps. */}
                <div className="flex flex-wrap gap-2 items-center justify-between">
                  <div className="flex gap-1.5 items-center">
                    <button
                      onClick={onSortDate}
                      className={`px-2.5 py-1.5 ${PILL_FLOOR}text-[10px] font-medium rounded border transition-colors ${
                        sortBy === "date"
                          ? PILL_ON
                          : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      Date{sortBy === "date" ? sortArrow : ""}
                    </button>
                    <button
                      onClick={() => setSortBy(sortBy === "activity" ? "date" : "activity")}
                      className={`px-2.5 py-1.5 ${PILL_FLOOR}text-[10px] font-medium rounded border transition-colors ${
                        sortBy === "activity"
                          ? PILL_ON
                          : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      Activity
                    </button>
                  </div>
                    
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setShowTagFiltersDialog(true)}
                      className={`px-2.5 py-1.5 ${PILL_FLOOR}text-[10px] font-medium rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors flex items-center gap-1.5 relative`}
                      title="Tag filters"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Tags
                      {selectedTagFilter && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#32B1FF] rounded-full"></span>
                      )}
                    </button>
                    <button
                      onClick={() => setShowMoreFilters(true)}
                      className={`px-2.5 py-1.5 ${PILL_FLOOR}text-[10px] font-medium rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors flex items-center gap-1.5 relative`}
                      title="More filters"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      More
                      {hasActiveAdvancedFilters && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#32B1FF] rounded-full"></span>
                      )}
                    </button>
                    <button
                      onClick={clearAllFilters}
                      className={`px-2 py-1.5 ${PILL_FLOOR}text-[10px] font-medium rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors`}
                      title="Clear all filters"
                    >
                      Clear
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* Second half of the pinned block — inside the sticky wrapper
                above, not a sibling of it. Its own opaque background repeats
                here because this div is what the rows actually pass behind. */}
            <div className="px-3 pb-3 space-y-2.5 border-b dark:border-gray-800 bg-white dark:bg-gray-900">
                {/* Phase 7 — conversation intelligence filters. Mounted on
                    voice and chat, the two modalities whose rows carry these
                    fields and whose analysis bands are gated the same way.
                    `modality` decides which controls exist: chat has five plus
                    visitor type, with no after-hours control at all. */}
                {isIntelligence && (
                  <ConversationIntelFilters
                    filters={intelFilters}
                    onChange={setIntelFilters}
                    onClear={() => setIntelFilters(DEFAULT_INTEL_FILTERS)}
                    modality={modalityFilter}
                  />
                )}

                {/* What a panel click narrowed the list to, shown WHERE the
                    list is and removable from there. */}
                <PanelFilterChip filter={panelFilter} onClear={clearPanelFilter} />
            </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {transcriptsLoading ? (
                <div className="p-4 text-gray-500 dark:text-gray-400">
                  {loadingProgress ? (
                    <div>
                      <div className="text-sm font-medium mb-2 text-center">
                        Loading {Math.round((loadingProgress.current / loadingProgress.total) * 100)}%
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-[#32B1FF] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : (
                    'Loading transcripts…'
                  )}
                </div>
              ) : transcriptsError ? (
                <div className="p-4 text-red-600 dark:text-red-400">{transcriptsError}</div>
              ) : visible.length === 0 ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                  {/* WHICH empty this is. A panel selection carries exact
                      conversation ids, but the gaps RPC is not date-scoped
                      while this list is — so a row can legitimately offer
                      three conversations and match none of the loaded ones.
                      The generic message reads as "this property has no such
                      conversations", which is a different and false claim. */}
                  {panelFilter
                    ? "None of those conversations fall in the selected date range"
                    : "No conversations found"}
                </div>
              ) : (
                <ul className={isIntelligence ? "max-md:px-3 max-md:pt-2 md:divide-y md:dark:divide-gray-800 pb-4" : "divide-y dark:divide-gray-800 pb-4"}>
                  {visible.map((row) => {
                    const isActive = row.id === selectedId;
                    // 2026-07-28 — Verde-only voice ingestion gap (Part 8 G3 /
                    // Part 9 §3): voice conversations exist as `conversations`
                    // rows but only ~31% get linked to phone_call_transcripts
                    // via phone_call_session_id. Rows without a session id
                    // will show the "no transcript" empty state on click.
                    // Surface it as a chip so the user knows before clicking.
                    const isVoiceRow = row.convo?.modality === 'voice';
                    const isOrphanVoice = isVoiceRow && !row.convo?.phone_call_session_id;
                    return (
                      <li key={row.id}>
                        <button
                          onClick={() => { setSelectedId(row.id); setSheetOpen(true); }}
                          className={isIntelligence
                            ? `w-full text-left px-4 py-3 flex items-start gap-3 transition min-h-[44px]
                               max-md:rounded-xl max-md:border max-md:border-gray-200 max-md:dark:border-gray-800 max-md:shadow-sm max-md:mb-2
                               hover:bg-[#F8FBFF] dark:hover:bg-gray-800
                               ${isActive ? "bg-[#EAF6FF] dark:bg-blue-900/30" : "bg-white dark:bg-gray-900"}`
                            : `w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#F8FBFF] dark:hover:bg-gray-800 transition ${isActive ? "bg-[#EAF6FF] dark:bg-blue-900/30" : "bg-white dark:bg-gray-900"}`}
                        >
                          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-[#EAF6FF] dark:bg-blue-900/30 border border-[#C7E7FF] dark:border-blue-800">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#32B1FF" strokeWidth="2">
                              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{row.displayName}</div>
                              {isOrphanVoice && (
                                <span
                                  className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 shrink-0"
                                  title="Voice call received — the transcript is still being processed or recovered by the automated backfill. Check back shortly."
                                >
                                  pending transcript
                                </span>
                              )}
                              {conversationTags[row.id] && (
                                <div className="flex gap-1">
                                  {(() => {
                                    const tag = conversationTags[row.id];
                                    const colors = getTagColors(tag);
                                    return (
                                      <span
                                        className={`w-2 h-2 rounded-full ${colors.bg} border ${colors.border}`}
                                        title={capitalizeTag(tag)}
                                      />
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {(row.date || "—")} · {(row.time || "—")}
                            </div>
                            {row.convo?.modality === "chat"
                              ? <ChatRowIntel row={row} />
                              : <RowIntel row={row} />}
                            {row._snippet && (
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                {row._snippet}
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* Closes the list off where it ends, which on a property with
                  few conversations is partway down a column the transcript
                  beside it makes tall. In the list's own flow, so a long list
                  simply meets it at the bottom of the scroll. */}
              <ListEndMarker
                shown={visible.length}
                total={totalConversations}
                complete={
                  !transcriptsLoading
                  && !transcriptsError
                  // `loadingMore` is asserted DIRECTLY and not inferred from the
                  // total, because the total is the thing that can be wrong.
                  // `total: count || 0` turns Supabase's null count — returned
                  // whenever an exact count is unavailable — into 0, which
                  // stops the loader's `offset < total` after page one AND
                  // collapses the third term below. Both from one cause, so a
                  // marker derived only from the total says "End of list" over
                  // page one of a list that never paged. "End of list" is a
                  // completeness claim even without the word "all".
                  && !loadingMore
                  && !(totalConversations > 0 && transcripts.length < totalConversations)
                }
              />
            </div>
            {/* Background-load indicator — gated on the data state (loaded < total)
                rather than a transient boolean, so it's reliably visible during background fill. */}
            {totalConversations > 0 && transcripts.length < totalConversations && (
              <div className="flex items-center justify-center gap-2 py-3 px-4 text-xs font-semibold text-[#32B1FF] dark:text-blue-300 border-t-2 border-[#32B1FF]/30 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 shrink-0">
                <svg className="animate-spin h-4 w-4 text-[#32B1FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Loading more conversations ({transcripts.length} of {totalConversations})…</span>
              </div>
            )}
          </aside>
  );

  // THE DETAIL PANE. Owns its own visibility; see its className.
  const conversationDetail = (
          /* Spec Part F — on a phone the detail is a full-screen sheet over the
              worklist, not a second pane stacked under it: at grid-cols-1 the
              two panes sat one above the other, so opening a conversation left
              the reader scrolling past the whole list to reach it. From `md` up
              it returns to being the right-hand column. Hidden entirely on a
              phone until a row is chosen, so the list owns the screen. */
          /* In the shell the detail is not a grid cell any more — it is the
              analysis column's other occupant, and the two take turns. Shown
              only once a conversation is chosen, and `md:flex-1` so it is
              BOUNDED: its transcript scroller is `flex-1 overflow-y-auto
              min-h-0`, which collapses to nothing inside a parent of natural
              height. On a phone it stays the full-screen sheet it already was.
              The desktop way back out is the shell's "Back to analysis". */
          <section
            className={!isIntelligence
              ? "md:col-span-7 lg:col-span-8 flex flex-col min-h-0 bg-white dark:bg-gray-950"
              : `flex-col min-h-0 bg-white dark:bg-gray-950 md:flex-1
              ${sheetOpen
                ? "flex fixed inset-0 z-40 md:static md:z-auto"
                : "hidden"}`}
          >
            {/* Phone-only header: the way back to the list. */}
            {isIntelligence && sheetOpen && (
              <div className="md:hidden flex items-center gap-2 px-3 py-2 border-b dark:border-gray-800 bg-white dark:bg-gray-900">
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="gi-link inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15 19l-7-7 7-7" />
                  </svg>
                  Conversations
                </button>
              </div>
            )}
            {transcriptsLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                  <svg className="animate-spin h-12 w-12 mx-auto text-[#32B1FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {loadingProgress ? (
                    <div className="mt-4 w-full max-w-xs mx-auto">
                      <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 text-center mb-3">
                        {Math.round((loadingProgress.current / loadingProgress.total) * 100)}%
                      </p>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div 
                          className="bg-[#32B1FF] h-3 rounded-full transition-all duration-300"
                          style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading transcripts...</p>
                  )}
                </div>
              </div>
            ) : selected ? (
              <>
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b dark:border-gray-800 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                      {selected.displayName} <span className={isIntelligence ? "text-gray-500 dark:text-gray-400" : "text-gray-400 dark:text-gray-600"}>·</span> {selected.date || "—"} {selected.time ? `· ${selected.time}` : ""}
                    </div>
                    <div className={isIntelligence ? "flex flex-wrap justify-end gap-2" : "flex gap-2"}>
                      {/* "View Transcript" dedicated-page button removed 2026-08-07 — the
                          /transcripts/:id page was broken; inline detail below is the view. */}
                      <button
                        type="button"
                        onClick={() => toggleTagging(selected.id)}
                        className={isIntelligence ? TAG_BUTTON : LEGACY_TAG_BUTTON}
                        title="Tag conversation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        Tag
                      </button>
                      {/* AI Tag Suggestion Button (only if AI enabled) */}
                      {aiEnabled && (
                        <>
                          {aiSuggestion && aiSuggestion.is_cached ? (
                            <button
                              type="button"
                              onClick={() => setShowAiSuggestion(true)}
                              className="px-3 py-1.5 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm flex items-center gap-1.5 transition-all"
                              title="Previously analyzed - Click to view results"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                              </svg>
                              View Analysis
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setAiCostAction('single');
                                setShowAiCostWarning(true);
                              }}
                              disabled={aiLoading}
                              className="px-3 py-1.5 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8] text-white text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                              title="Generate AI tag suggestion"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 7H7v6h6V7z" />
                                <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                              </svg>
                              {aiLoading ? "Analyzing..." : "AI Suggest Tag"}
                            </button>
                          )}
                            
                          {/* AI Summary Button */}
                          {aiSummary && aiSummary.is_cached ? (
                            <button
                              type="button"
                              onClick={() => setShowAiSummary(true)}
                              className="px-3 py-1.5 rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-sm flex items-center gap-1.5 transition-all"
                              title="Previously analyzed - Click to view results"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                              </svg>
                              View Summary
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setAiCostAction('summary');
                                setShowAiCostWarning(true);
                              }}
                              disabled={aiSummaryLoading}
                              className="px-3 py-1.5 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                              title="Generate AI conversation summary"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                              </svg>
                              {aiSummaryLoading ? "Generating..." : "Generate Summary"}
                            </button>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        onClick={exportSelectedAsCSV}
                        className={isIntelligence ? EXPORT_CSV_BUTTON : LEGACY_TOOLBAR_BUTTON}
                        title="Export this conversation to CSV"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV
                      </button>
                      <button
                        type="button"
                        onClick={exportSelectedAsPDF}
                        className={isIntelligence ? EXPORT_PDF_BUTTON : LEGACY_TOOLBAR_BUTTON}
                        title="Export this conversation to PDF"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export PDF
                      </button>
                    </div>
                  </div>
                  {/* Tag */}
                  {(() => {
                    const tag = conversationTags[selected.id];
                    if (!tag) return null;
                    const colors = getTagColors(tag);
                    return (
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}
                        >
                          {capitalizeTag(tag)}
                          <button
                            onClick={() => handleRemoveTag(selected.id)}
                            className={`ml-1.5 rounded-full p-0.5 ${colors.hoverBg}`}
                            title="Remove tag"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Tag selector modal */}
                {taggingConversationId === selected.id && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 dark:bg-black/40" onClick={() => setTaggingConversationId(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 max-w-xs w-full mx-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Select a Tag</h3>
                        <button
                          onClick={() => setTaggingConversationId(null)}
                          className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {availableTags.map((tag) => {
                          const colors = getTagColors(tag);
                          return (
                            <button
                              key={tag}
                              onClick={() => handleAddTag(selected.id, tag)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg ${colors.bg} ${colors.hoverBg} ${colors.text} transition-colors border ${colors.border || ""} text-left`}
                            >
                              {capitalizeTag(tag)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                  
                {/* Messages section */}
                <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-950 min-h-0">
                  <div className="space-y-3 px-3 py-3 pb-20">
                  {/* The rebuilt pane is part of the analysis surface (view=intelligence).
                      Off it, a voice conversation renders exactly what it did
                      before: the VoiceCallDetails panel above the transcript.
                      Gating the pane alone dropped that panel entirely, because
                      the else-branch is the chat rendering. */}
                  {!isIntelligence && selected?.convo?.modality === "voice" && (
                    <VoiceCallDetails call={selectedCall} />
                  )}
                  {/* Widened to chat: the pane decides which blocks exist from the
                      modality, never from whether a payload came back null —
                      `detail.call` is null on EVERY chat row, so a null-check
                      could not tell a chat row from a voice call whose
                      recording failed to link. */}
                  {isIntelligence && (selected?.convo?.modality === "voice" || selected?.convo?.modality === "chat") ? (
                    <ConversationDetailPane
                      conversationId={selectedId}
                      clientId={view?.supabaseClientId}
                      convo={selected.convo}
                      modality={selected.convo.modality}
                      fallbackName={selected.displayName}
                      getMsgSide={getMsgSide}
                      getMsgText={getMsgText}
                      getMsgTime={getMsgTime}
                      formatTime={formatTimeOnlySafe}
                    />
                  ) : (
                  (() => {
                    // Prefer messages fetched from conversation_messages; fall back to
                    // any legacy shape the router state may have carried on the raw row.
                    const msgs = (selectedMessages && selectedMessages.length > 0)
                      ? selectedMessages
                      : normalizeMessages(selected.convo);
                    if (!msgs.length && selectedMessagesLoading) {
                      return (
                        <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-6" role="status">Loading messages…</div>
                      );
                    }
                    if (!msgs.length) {
                      // Diagnostic empty state: for voice conversations, the raw call
                      // transcript is stored in phone_call_transcripts + linked via
                      // phone_call_session_id. If the session id is null the ingestion
                      // pipeline never wrote a phone_call_sessions row for this call —
                      // usually a Voiceflow/Twilio-side data-pipeline issue, not a
                      // read-side bug.
                      const isVoice = selected.convo?.modality === "voice";
                      const missingSession = isVoice && !selected.convo?.phone_call_session_id;
                      const msg = missingSession
                        ? "This call's transcript isn't available yet. Voice transcripts are processed — and older ones recovered — automatically, so check back shortly. If it still hasn't appeared after 24 hours, contact support."
                        : isVoice
                          ? "Call session exists but no turns were captured for it."
                          : "No messages captured for this conversation.";
                      return (
                        <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-6 px-6 max-w-md mx-auto">{msg}</div>
                      );
                    }
                    return msgs.map((m, i) => {
                      const side = getMsgSide(m);
                      const text = getMsgText(m);
                      const timeRaw = getMsgTime(m);
                      const time12h = formatTimeOnlySafe(timeRaw);

                      const prev = i > 0 ? msgs[i - 1] : null;
                      const prevT = prev ? getMsgTime(prev) : null;
                      const delta =
                        prev &&
                        parseTime12hToMs(timeRaw) != null &&
                        parseTime12hToMs(prevT) != null &&
                        parseTime12hToMs(timeRaw) >= parseTime12hToMs(prevT)
                          ? formatDelta(parseTime12hToMs(timeRaw) - parseTime12hToMs(prevT))
                          : null;

                      const isRight = side === "right";
                      return (
                        <div key={i} className="space-y-1">
                          {i > 0 && delta && (
                            <div className="flex items-center justify-center">
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                {delta}
                              </div>
                            </div>
                          )}
                          <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm border ${
                                isRight
                                  ? "bg-[#328DFF] text-white border-[#1c8fd6]"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
                              }`}
                            >
                              <div className="whitespace-pre-wrap break-words">
                                {text || <span className="opacity-60">(no text)</span>}
                              </div>
                              {time12h && (
                                <div className={`mt-1 text-[10px] ${isRight ? "text-white/80" : "text-gray-500 dark:text-gray-400"}`}>
                                  {time12h}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()
                  )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm">Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </section>
  );

  return (
    <div className="bg-white dark:bg-gray-950 h-screen flex flex-col overflow-hidden transition-colors duration-200">
      <AdminHeader username={username} initials={username.slice(0, 2).toUpperCase()} activePage="Projects" />

      {/* Project name banner */}
      {projectName && (
        <div className="px-4 py-2 border-b dark:border-gray-800 bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-[#EAF6FF] dark:bg-blue-900/30 border border-[#C7E7FF] dark:border-blue-800 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img 
                src={projectLogo || "/Rigsby.png"} 
                alt={projectName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "/Rigsby.png";
                }}
              />
            </div>
            <div>
              <span className="font-medium">Project:</span> {projectName}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0 border-t dark:border-gray-800">
        <NavSidebar activePage={navActiveKeyForModality(modalityFilter, { showChat: view?.showChat, showVoice: view?.showVoice })} />
        {(modalityFilter === "voice" || modalityFilter === "chat") ? (
          /* SPEC V6 §B.1 — My Agents → Phone / Chat IS the ConversationPage: a
             permanent 340px rail + an independent-scroll analysis column, one
             component on two routes. It replaces the v1 band-above-list entirely,
             and the ?view=intelligence toggle is retired (modality alone decides).
             The bare master-detail below stays for a null-modality transcript URL. */
          <ConversationPage
            clientId={projectId}
            range={{ start: startDate, end: endDate }}
            modality={modalityFilter}
          />
        ) : (
          /* No analysis band — My Agents -> Chat, My Agents -> Voice, any other
             modality. Unchanged: the bare master-detail at its old fixed height,
             each pane scrolling internally. PANEL_H is still true here, because
             here this grid really is the top of the page. */
          <div className="flex-1 min-h-0">
            <div
              className="grid grid-cols-1 md:grid-cols-12 min-h-0 h-full"
              style={{ height: PANEL_H }}
            >
              {conversationRail}
              {conversationDetail}
            </div>
          </div>
        )}
      </div>

      {/* More Filters Modal - copy from ClientProjectDetailPage */}
      {showMoreFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMoreFilters(false)}></div>
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Advanced Filters</h3>
              </div>
              <button onClick={() => setShowMoreFilters(false)} className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Conversation Length</label>
                <select
                  value={messageLengthFilter}
                  onChange={(e) => setMessageLengthFilter(e.target.value)}
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Lengths</option>
                  <option value="short">Short (1-5 messages)</option>
                  <option value="medium">Medium (6-15 messages)</option>
                  <option value="long">Long (16+ messages)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time of Day</label>
                <select
                  value={timeOfDayFilter}
                  onChange={(e) => setTimeOfDayFilter(e.target.value)}
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">Any Time</option>
                  <option value="morning">🌅 Morning (6 AM - 12 PM)</option>
                  <option value="afternoon">☀️ Afternoon (12 PM - 6 PM)</option>
                  <option value="evening">🌆 Evening (6 PM - 12 AM)</option>
                  <option value="night">🌙 Night (12 AM - 6 AM)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2.5">Visitor Type</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setVisitorTypeFilter("all")}
                    className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors border ${
                      visitorTypeFilter === "all"
                        ? PILL_ON
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setVisitorTypeFilter("named")}
                    className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors border ${
                      visitorTypeFilter === "named"
                        ? PILL_ON
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    Named
                  </button>
                  <button
                    onClick={() => setVisitorTypeFilter("guest")}
                    className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors border ${
                      visitorTypeFilter === "guest"
                        ? PILL_ON
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    Guests
                  </button>
                </div>
              </div>

              {aiEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">AI Analysis Status</label>
                  <select
                    value={aiAnalysisFilter}
                    onChange={(e) => setAiAnalysisFilter(e.target.value)}
                    className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Conversations</option>
                    <option value="tagged">🤖 AI Tagged Only</option>
                    <option value="summarized">📝 AI Summarized Only</option>
                    <option value="both">✨ Both (Tagged & Summarized)</option>
                    <option value="none">⚪ Not Analyzed</option>
                  </select>
                </div>
              )}

              {hasActiveAdvancedFilters && (
                <div className="pt-4 border-t dark:border-gray-800">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-[#32B1FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <p className="font-medium">Active filters:</p>
                      <ul className="mt-1 space-y-0.5">
                        {messageLengthFilter !== "all" && <li>• Length: {messageLengthFilter}</li>}
                        {timeOfDayFilter !== "all" && <li>• Time: {timeOfDayFilter}</li>}
                        {visitorTypeFilter !== "all" && <li>• Visitor: {visitorTypeFilter}</li>}
                        {aiAnalysisFilter !== "all" && <li>• AI: {aiAnalysisFilter}</li>}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-3">
              <button
                onClick={() => {
                  setMessageLengthFilter("all");
                  setTimeOfDayFilter("all");
                  setVisitorTypeFilter("all");
                  setAiAnalysisFilter("all");
                }}
                disabled={!hasActiveAdvancedFilters}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Filters
              </button>
              <button
                onClick={() => setShowMoreFilters(false)}
                className="px-6 py-2 text-sm font-medium rounded-lg bg-[#32B1FF] hover:bg-[#2A9FEB] text-white transition-colors shadow-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Filters Dialog */}
      {showTagFiltersDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowTagFiltersDialog(false)}
          ></div>
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tag Filters</h3>
              </div>
              <button
                onClick={() => setShowTagFiltersDialog(false)}
                className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {/* Bulk AI Actions Button */}
              {aiEnabled && unanalyzedCount > 0 && (
                <div className="pb-3 border-b dark:border-gray-800">
                  <button
                    onClick={() => {
                      setShowTagFiltersDialog(false);
                      setShowBulkAiMenu(true);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8] text-white text-sm font-medium transition-all shadow-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 7H7v6h6V7z" />
                      <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                    </svg>
                    Bulk AI Actions ({unanalyzedCount})
                  </button>
                </div>
              )}

              {/* All Tags Button */}
              <button
                onClick={() => setSelectedTagFilter(null)}
                className={`w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-between ${
                  selectedTagFilter === null
                    ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border dark:border-gray-700"
                }`}
              >
                <span>All Tags</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  selectedTagFilter === null 
                    ? 'bg-white/20 text-current'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {visible.length}
                </span>
              </button>

              {/* Tag Buttons */}
              {availableTags.map((tag) => {
                const colors = getTagColors(tag);
                const tagCount = visible.filter(t => {
                  const convoTag = conversationTags[t.id];
                  return convoTag && convoTag.toLowerCase() === tag.toLowerCase();
                }).length;
                
                return (
                  <button
                    key={tag}
                    onClick={() => setSelectedTagFilter(selectedTagFilter === tag ? null : tag)}
                    className={`w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-between border ${
                      selectedTagFilter === tag
                        ? `${colors.bg} ${colors.text} ${colors.border} shadow-sm`
                        : `bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700`
                    }`}
                  >
                    <span>{capitalizeTag(tag)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      selectedTagFilter === tag 
                        ? 'bg-white/20 text-current'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {tagCount}
                    </span>
                  </button>
                );
              })}

              {/* Active Filter Summary */}
              {selectedTagFilter && (
                <div className="pt-4 border-t dark:border-gray-800">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-[#32B1FF] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <p className="font-medium">Active filter:</p>
                      <p className="mt-1">Showing only: {capitalizeTag(selectedTagFilter)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-3">
              <button
                onClick={() => setSelectedTagFilter(null)}
                disabled={!selectedTagFilter}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                onClick={() => setShowTagFiltersDialog(false)}
                className="px-6 py-2 text-sm font-medium rounded-lg bg-[#32B1FF] hover:bg-[#2A9FEB] text-white transition-colors shadow-sm"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Cost Warning Modal */}
      {aiEnabled && showAiCostWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAiCostWarning(false)}></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">AI Analysis Confirmation</h3>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  {aiCostAction === 'single' && (
                    <>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        Analyzing current conversation
                      </p>
                      <p className="text-xs">Tag suggestion for this conversation only.</p>
                    </>
                  )}
                  {aiCostAction === 'summary' && (
                    <>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        Generating summary for current conversation
                      </p>
                      <p className="text-xs">Full summary for this conversation only.</p>
                    </>
                  )}
                  {aiCostAction === 'batch' && (
                    <>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        Batch analyzing {untaggedCount} conversations
                      </p>
                      <p className="text-xs">Tag suggestions for all unanalyzed conversations.</p>
                    </>
                  )}
                  <div className="pt-2 border-t dark:border-gray-800">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      💡 Results are saved permanently.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAiCostWarning(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAiCostWarning(false);
                  if (aiCostAction === 'single') {
                    proceedWithSingleAnalysis();
                  } else if (aiCostAction === 'summary') {
                    handleGenerateSummary();
                  } else {
                    proceedWithBatchAnalysis();
                  }
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8] text-white font-medium transition-colors shadow-sm"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tag Suggestion Popup */}
      {aiEnabled && showAiSuggestion && aiSuggestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAiSuggestion(false)}></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-lg mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Tag Suggestion</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {aiSuggestion.is_cached ? '💾 Cached result' : '✨ New analysis'}
                </p>
              </div>
              <button onClick={() => setShowAiSuggestion(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800">
                <div className="text-3xl">{tagColors[aiSuggestion.tag?.toLowerCase()]?.emoji || '🏷️'}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{capitalizeTag(aiSuggestion.tag)}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                      {Math.round(aiSuggestion.confidence * 100)}% confident
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{aiSuggestion.reasoning}</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span>Confidence Score</span>
                  <span>{Math.round(aiSuggestion.confidence * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] h-2 rounded-full transition-all"
                    style={{ width: `${aiSuggestion.confidence * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAiSuggestion(false)} className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium transition-colors">
                Dismiss
              </button>
              <button onClick={handleApplyAiTag} className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8] text-white font-medium transition-colors shadow-sm">
                Apply Tag
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Summary Panel */}
      {aiEnabled && showAiSummary && aiSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAiSummary(false)}></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-2xl mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Conversation Summary</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {aiSummary.is_cached ? '💾 Cached result' : '✨ New analysis'}
                </p>
              </div>
              <button onClick={() => setShowAiSummary(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">📝 Overview</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{aiSummary.summary}</p>
              </div>

              {/* 2026-08-05 — Chat Ingestion Change Log §3/§5: pre-2026-08-05
                  chat enrichment is unreliable because buildConversationText
                  fed Claude bot-only transcripts (user messages were dropped
                  by parseLogs). Show a warning so users don't trust sentiment,
                  outcome, summary, etc. on old chat records. */}
              {(() => {
                const convo = selected?.convo;
                const isChat = convo?.modality === 'chat';
                const startedAt = convo?.started_at || '';
                const isPreFix = startedAt && startedAt.slice(0, 10) < '2026-08-05';
                const isCached = aiSummary?.is_cached === true;
                if (isChat && isPreFix && isCached) {
                  return (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <span className="font-semibold">Warning: Unreliable analysis.</span> This chat was
                        ingested before the 2026-08-05 user-message fix. Its AI summary was generated
                        from a bot-only transcript, so sentiment, outcome, and other enrichment fields
                        may be inaccurate. Re-analyze after the transcript is re-parsed for reliable results.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">🎯 Customer Intent</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{aiSummary.customerIntent}</p>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="text-2xl">
                  {aiSummary.sentiment === 'positive' ? '😊' : aiSummary.sentiment === 'negative' ? '😔' : '😐'}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sentiment</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{aiSummary.sentiment}</p>
                </div>
              </div>

              {aiSummary.keyPoints && aiSummary.keyPoints.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">🔑 Key Points</h4>
                  <ul className="space-y-1.5">
                    {aiSummary.keyPoints.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-[#32B1FF] mt-0.5">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {aiSummary.actionItems && aiSummary.actionItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">✅ Action Items</h4>
                  <ul className="space-y-1.5">
                    {aiSummary.actionItems.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-indigo-500 mt-0.5">→</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {aiSummary.recommendedTag && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold">Recommended Tag:</span> <span className="capitalize">{aiSummary.recommendedTag}</span>
                  </span>
                  {!conversationTags[selected?.id] && (
                    <button
                      onClick={() => handleAddTag(selected.id, normalizeTag(aiSummary.recommendedTag))}
                      className="px-3 py-1 text-xs font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                    >
                      Apply Tag
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowAiSummary(false)}
              className="w-full mt-6 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-colors shadow-sm"
            >
              Close Summary
            </button>
          </div>
        </div>
      )}

      {/* Batch Progress Modal */}
      {aiEnabled && showBatchProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 p-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#32B1FF] animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Analyzing Conversations
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Processing {batchProgress.current} of {batchProgress.total} conversations...
              </p>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] h-2 rounded-full transition-all"
                  style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk AI Actions Dialog */}
      {aiEnabled && showBulkAiMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBulkAiMenu(false)}></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 7H7v6h6V7z" />
                      <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bulk AI Actions</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">AI-powered batch processing</p>
                  </div>
                </div>
                <button onClick={() => setShowBulkAiMenu(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-[#32B1FF]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">Bulk AI Auto-Tag</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      Analyze and tag {unanalyzedCount} unanalyzed conversation{unanalyzedCount !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-[#32B1FF] font-semibold">
                          {unanalyzedCount} conversation{unanalyzedCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBulkAiMenu(false);
                    setUntaggedCount(unanalyzedCount);
                    setAiCostAction('batch');
                    setShowAiCostWarning(true);
                  }}
                  disabled={batchAnalyzing}
                  className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8] disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {batchAnalyzing ? 'Processing...' : 'Start Auto-Tagging'}
                </button>
              </div>

              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <p className="font-semibold mb-1">Smart Processing</p>
                    <ul className="space-y-0.5 text-blue-600 dark:text-blue-400">
                      <li>✓ Only unanalyzed conversations</li>
                      <li>✓ Results saved permanently</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {showNotification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNotification(false)}></div>
          
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border dark:border-gray-800 w-full max-w-md mx-4 overflow-hidden">
            <div className={`h-2 ${
              notification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
              notification.type === 'error' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
              notification.type === 'info' ? 'bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB]' :
              'bg-gradient-to-r from-yellow-500 to-amber-600'
            }`}></div>
            
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  notification.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                  notification.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                  notification.type === 'info' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  'bg-yellow-100 dark:bg-yellow-900/30'
                }`}>
                  {notification.type === 'success' && (
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                  {notification.type === 'error' && (
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  {notification.type === 'info' && (
                    <svg className="w-6 h-6 text-[#32B1FF]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                  {notification.type === 'warning' && (
                    <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {notification.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                    {notification.message}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowNotification(false)}
                className={`w-full px-4 py-2.5 rounded-lg font-medium text-white transition-all shadow-sm hover:shadow-md ${
                  notification.type === 'success' ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' :
                  notification.type === 'error' ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700' :
                  notification.type === 'info' ? 'bg-gradient-to-r from-[#32B1FF] to-[#2A9FEB] hover:from-[#2A9FEB] hover:to-[#228ED8]' :
                  'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700'
                }`}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetailPage;
